import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { AnyFieldDef } from '@resource-kit/model';
import { renderStatusBadge } from '../registry/field-type-registry';
import { FormView } from '../form/FormView';
import { useFormViewSlots } from '../form/slots';
import { Button } from '../primitives/Button';
import { ResourceDetailActions } from './ResourceDetailActions';
import { Trash2 } from 'lucide-react';
import { useResourceKit } from '@resource-kit/core/react';
import type { ResourceBundle } from '@resource-kit/controllers/react';

interface ResourceDetailPanelProps<T, F extends Record<string, AnyFieldDef>> {
  item: T;
  bundle: ResourceBundle<T, F>;
  columns: ColumnDef<T>[];
  embedded?: boolean;
  /** Render the record PageHeader when the embedding surface owns an opening. */
  showEmbeddedHeader?: boolean;
  /**
   * The operator may edit this record. Editing is an IN-PLACE mode switch
   * owned by `FormView` (#1797) — there is no edit dialog to open, so this is
   * a capability flag, not a callback.
   */
  canEdit?: boolean;
  onDelete?: () => void;
  /** True while a delete is pending; the Delete button is disabled (#2814). */
  deleteBusy?: boolean;
  /** Actions supplied by the owning surface, such as drawer navigation. */
  additionalActions?: ReactNode;
  /**
   * Called when a `detail.actions` action whose `afterAction` is `close`
   * succeeds — the drawer closes it, the standalone page navigates back
   * (#1417). Unset ⇒ such an action just refetches (its default). It also
   * reaches the resource's header commands as `leaveDetail` in the
   * `headerActions` ctx, so a command that removes the record can leave the
   * detail (#2793, #2802).
   */
  onClose?: () => void;
}

export function ResourceDetailPanel<T, F extends Record<string, AnyFieldDef>>({
  item,
  bundle,
  columns,
  embedded = true,
  showEmbeddedHeader = false,
  canEdit = false,
  onDelete,
  deleteBusy = false,
  additionalActions,
  onClose,
}: ResourceDetailPanelProps<T, F>) {
  const { t, access } = useResourceKit();
  const slots = useFormViewSlots();

  const rawTitle =
    bundle.host.detail?.title?.(item) ??
    t(bundle.host.label, { defaultValue: bundle.host.label }) + ' ' + t('rk.resource.details');
  const title = rawTitle;
  const subtitle = bundle.host.detail?.subtitle?.(item);

  // #1291 — detail-header status badge derived from a schema enum field named
  // by `detail.statusField`. Resolve the field def + wire value; the shared
  // `renderStatusBadge` returns null for an empty value or a non-enum field.
  const statusFieldKey = bundle.host.detail?.statusField;
  const statusField =
    statusFieldKey != null
      ? columns.find((column) => {
          const field = column.meta?.field;
          return field != null && String(field.key) === String(statusFieldKey);
        })?.meta?.field
      : undefined;
  const titleAdornment =
    statusField?.type === 'enum'
      ? renderStatusBadge(statusField, (item as Record<string, unknown>)[String(statusFieldKey)], t)
      : undefined;

  // The panel owns one half of the change-history gate — WHO is asking. The
  // other half (whether the ledger covers this resource) and the rendering are
  // `FormView`'s, which puts the history on the shared tab rail (#1804).
  //
  // "Who" is a disjunction since #1964: the admin role OR the `audit:read`
  // grant. Both halves are needed because the backend's `requirePermission`
  // short-circuits on the admin role — an admin reaches the ledger without
  // holding the key, so a grant-only check here would hide a tab the API
  // would have served.
  //
  // The same identity answers the resource's own gated tabs (#1921). The panel
  // computes no verdict of its own: it hands the identity to the resource,
  // which knows which of ITS tab ids the answer belongs to. A resource that
  // declares no `tabGates` passes `undefined`, which `FormView` reads as
  // fail-closed — and never as a stray key it would fail loud on.
  //
  // The identity arrives through the kit's services seam (#2165) rather than
  // the host's auth hook, and it is handed on WHOLE: `access` IS the
  // `ResourceTabGateContext` a schema's `tabGates` is declared over, so there
  // is no place left to rebuild it slightly differently on one of the two
  // surfaces that ask.
  const tabGates = bundle.host.detail?.tabGates?.(access);

  const idValue = item[bundle.model.idField] ?? (item as { id?: unknown }).id;
  const entityId = idValue != null ? String(idValue) : '';

  // Compose every detail action source once. FormView hands the node to the
  // shared PageHeader on both the standalone page and embedded drawer.
  const headerActions = bundle.host.detail?.headerActions?.(item, { leaveDetail: onClose });
  const detailActions = bundle.host.detail?.actions;
  const hasDetailActions = detailActions != null && detailActions.length > 0;
  const hasLegacyActions = !!onDelete;
  const hasHeaderActions = headerActions != null;
  const hasAdditionalActions = additionalActions != null;
  // The Edit affordance is NOT here any more — `FormView` renders it next to
  // these, because clicking it flips the same tree into edit rather than
  // opening a second surface.
  const actions =
    hasHeaderActions || hasLegacyActions || hasDetailActions || hasAdditionalActions ? (
      <>
        {headerActions}
        {additionalActions}
        {onDelete && (
          <Button variant="destructive" size="sm" onClick={onDelete} disabled={deleteBusy}>
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            {t('rk.ui.delete')}
          </Button>
        )}
        {hasDetailActions && (
          <ResourceDetailActions
            row={item}
            rowId={entityId}
            slug={bundle.host.slug}
            actions={detailActions}
            leaveDetail={onClose}
          />
        )}
      </>
    ) : undefined;

  return (
    <FormView
      bundle={bundle}
      columns={columns}
      record={item}
      recordId={entityId || undefined}
      canEdit={canEdit}
      // Kept as an explicit prop rather than read from the slot inside
      // `FormView`: the existing audit-tab gate tests inject this boolean, and
      // preserving their injection form is why the relay stands. Collapsing it
      // is a separate step.
      canViewAudit={slots.changeHistory.canView(access)}
      tabGates={tabGates}
      title={title}
      subtitle={subtitle}
      titleAdornment={titleAdornment}
      embedded={embedded}
      showEmbeddedHeader={showEmbeddedHeader}
      actions={actions}
    />
  );
}
